import './styles.css';
import coordinateMap from '../coordinate_map.json';
import muscleTraits from '../muscle_traits.json';
import { figureTraitsForId } from './lib/figure-metadata.js';
import { initializeSiteCommunity } from './site-community.js';

const FIGURE_COUNT = 236;
const FIGURE_BASE_URL = 'https://pub-3d881e5f5bfb4f939ac3507d8e4434cb.r2.dev/figures';
const TOKEN_PAGE_BASE_URL = 'https://littlepinkmen.com/figures/';
const DEEP_ZOOM_BASE_URL = 'https://muscle.blob.core.windows.net/muscledzi';
const MOSAIC_ROUTE = '/gallery';
const MOSAIC_PATH = '/gallery/';
const collectionPath = normalizedPath(location.pathname) === MOSAIC_ROUTE ? MOSAIC_PATH : '/';
const defaultTitle = collectionPath === MOSAIC_PATH
  ? 'Little Pink Men Mosaic — M.U.S.C.L.E. Zone'
  : 'Little Pink Men — M.U.S.C.L.E. Zone';

const figures = Array.from({ length: FIGURE_COUNT }, (_, offset) => {
  const id = offset + 1;
  const traits = figureTraitsForId(muscleTraits, id);
  return {
    id,
    name: traits.NAME || `Figure ${id}`,
    traits,
    search: [id, ...Object.values(traits)].join(' ').toLowerCase(),
  };
});

const ui = {
  search: byId('figure-search'),
  sort: byId('figure-sort'),
  resultCount: byId('result-count'),
  readStatus: byId('read-status'),
  grid: byId('figure-grid'),
  galleryTools: byId('gallery-tools'),
  galleryMode: byId('gallery-mode'),
  exploreMode: byId('explore-mode'),
  viewer: byId('deep-zoom-viewer'),
  zoomIn: byId('zoom-in'),
  zoomOut: byId('zoom-out'),
  zoomHome: byId('zoom-home'),
  figureDialog: byId('figure-dialog'),
  figureClose: byId('figure-close'),
  figureImage: byId('figure-dialog-image'),
  figureTitle: byId('figure-dialog-title'),
  figurePhase: byId('figure-phase'),
  traits: byId('figure-traits'),
  marketSummary: byId('market-summary'),
};

const cards = new Map();
const state = {
  selectedId: null,
  figureOpener: null,
  mosaic: null,
  openSeadragon: null,
  mosaicView: 'front',
  mosaicPointer: null,
  suppressMosaicClick: null,
  collectionPath,
};

initialize();

function initialize() {
  createCards();
  renderGallery();
  bindEvents();
  bindLaunchSignup();
  initializeSiteCommunity();
  setCollectionMode(collectionPath === MOSAIC_PATH ? 'explore' : 'gallery');
  document.title = defaultTitle;
  const galleryNav = document.querySelector('.site-nav a[href="/gallery/"]');
  if (collectionPath === MOSAIC_PATH) galleryNav?.setAttribute('aria-current', 'page');
  else galleryNav?.removeAttribute('aria-current');
  updateCanonical(null);
  handleRoute();
}

function bindEvents() {
  ui.search.addEventListener('input', renderGallery);
  ui.sort.addEventListener('change', renderGallery);
  ui.zoomIn.addEventListener('click', () => zoomMosaic(1.35));
  ui.zoomOut.addEventListener('click', () => zoomMosaic(0.74));
  ui.zoomHome.addEventListener('click', () => state.mosaic?.viewport.goHome());
  ui.viewer.addEventListener('keydown', handleMosaicKeys);
  document.addEventListener('pointerdown', handleMosaicPointerDown, true);
  document.addEventListener('pointerup', handleMosaicPointerUp, true);
  document.addEventListener('pointercancel', clearMosaicPointer, true);
  document.querySelectorAll('[data-mosaic-view]').forEach((button) => {
    button.addEventListener('click', () => setMosaicView(button.dataset.mosaicView));
  });
  ui.figureClose.addEventListener('click', closeFigure);
  ui.figureDialog.addEventListener('close', () => {
    if (figureIdFromPath(location.pathname)) history.replaceState({}, '', state.collectionPath);
    updateCanonical(null);
    state.selectedId = null;
    document.title = defaultTitle;
    if (state.figureOpener?.isConnected) state.figureOpener.focus();
    state.figureOpener = null;
  });
  globalThis.addEventListener('popstate', handleRoute);
}

function bindLaunchSignup() {
  const form = byId('launch-signup');
  const status = byId('launch-signup-status');
  const startedAt = byId('launch-signup-started-at');
  const submit = form.querySelector('button[type="submit"]');

  startedAt.value = String(Date.now());
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    submit.disabled = true;
    status.textContent = 'Adding you to the launch list…';

    try {
      const response = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.fromEntries(new FormData(form))),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || 'Signup did not go through.');
      form.reset();
      startedAt.value = String(Date.now());
      status.textContent = 'You’re on the list. We’ll email you when Little Pink Men goes live.';
    } catch (error) {
      status.textContent = errorText(error);
    } finally {
      submit.disabled = false;
    }
  });
}

function createCards() {
  for (const figure of figures) {
    const card = element('a', 'figure-card');
    card.href = `/figures/${figure.id}`;
    card.dataset.figureId = String(figure.id);
    card.dataset.phase = 'under-construction';
    card.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      history.pushState({ figureId: figure.id }, '', card.href);
      openFigure(figure.id, card);
    });

    const imageFrame = element('span', 'figure-image-frame');
    const image = element('img', 'figure-image');
    image.src = figureImageUrl(figure.id, 280);
    image.alt = `${figure.name}, figure ${figure.id}`;
    image.loading = 'lazy';
    image.decoding = 'async';
    const constructionMarker = element(
      'span',
      'construction-marker',
      '🚧 UNDER CONSTRUCTION 🚧',
    );
    constructionMarker.setAttribute('aria-hidden', 'true');
    imageFrame.append(image, constructionMarker);

    const copy = element('span', 'figure-card-copy');
    copy.append(
      element('span', 'figure-number', `#${String(figure.id).padStart(3, '0')}`),
      element('span', 'card-name-banner', figure.name),
    );
    card.append(imageFrame, copy);
    cards.set(figure.id, card);
  }
}

function renderGallery() {
  const query = ui.search.value.trim().toLowerCase();
  const sort = ui.sort.value;
  const visible = figures
    .filter((figure) => !query || figure.search.includes(query))
    .sort((left, right) =>
      sort === 'name' ? left.name.localeCompare(right.name) || left.id - right.id : left.id - right.id,
    );

  ui.grid.replaceChildren(...visible.map((figure) => cards.get(figure.id)));
  ui.resultCount.textContent = `${visible.length} figure${visible.length === 1 ? '' : 's'}`;
}

function handleRoute() {
  const rawId = figureIdFromPath(location.pathname);
  if (rawId === null) {
    if (ui.figureDialog.open) ui.figureDialog.close();
    return;
  }
  const id = Number(rawId);
  if (!Number.isInteger(id) || id < 1 || id > FIGURE_COUNT) {
    ui.readStatus.textContent = `Figure ${rawId} does not exist. Choose a number from 1 to ${FIGURE_COUNT}.`;
    ui.readStatus.setAttribute('role', 'alert');
    return;
  }
  openFigure(id);
}

function openFigure(figureId, opener = null) {
  const figure = figures[figureId - 1];
  if (!figure) return;
  state.selectedId = figureId;
  state.figureOpener = opener?.isConnected ? opener : null;
  updateCanonical(figureId);

  ui.figureTitle.textContent = `#${String(figureId).padStart(3, '0')} ${figure.name}`;
  ui.figureImage.src = figureImageUrl(figureId, 500);
  ui.figureImage.alt = `${figure.name}, figure ${figureId}`;
  ui.figurePhase.textContent = 'AUCTION UNDER CONSTRUCTION';
  renderTraits(figure.traits);
  ui.marketSummary.textContent =
    'This figure’s MATT auction is not live yet. Join the launch list on the gallery page and we’ll email you when bidding opens.';
  document.title = `${figure.name} #${figureId} — Little Pink Men`;
  if (!ui.figureDialog.open) ui.figureDialog.showModal();
}

function closeFigure() {
  updateCanonical(null);
  ui.figureDialog.close();
}

function setCollectionMode(mode) {
  const exploring = mode === 'explore';
  ui.galleryTools.hidden = exploring;
  ui.galleryMode.hidden = exploring;
  ui.exploreMode.hidden = !exploring;
  if (exploring) {
    initializeMosaic()
      .then(() => globalThis.setTimeout(() => state.mosaic?.viewport.resize(), 0))
      .catch((error) => {
        ui.readStatus.textContent = `The mosaic could not load. The complete gallery remains available. ${errorText(error)}`;
        setCollectionMode('gallery');
      });
  }
}

async function initializeMosaic() {
  if (state.mosaic) return;
  const { default: OpenSeadragon } = await import('openseadragon');
  state.openSeadragon = OpenSeadragon;
  const figureSize = 5533;
  state.mosaic = OpenSeadragon({
    element: ui.viewer,
    tileSources: mosaicTileSource(state.mosaicView, 16 * figureSize, 15 * figureSize),
    showNavigationControl: false,
    showNavigator: false,
    immediateRender: false,
    maxZoomPixelRatio: 2,
    minZoomImageRatio: 1,
    visibilityRatio: 1,
    constrainDuringPan: true,
    homeFillsViewer: false,
    zoomPerScroll: 1.35,
    gestureSettingsMouse: { clickToZoom: false, flickEnabled: true },
    gestureSettingsTouch: { pinchToZoom: true, pinchRotate: false, flickEnabled: true },
  });
  state.mosaic.addHandler('open', () => addMosaicOverlays(figureSize));
}

function mosaicTileSource(view, width, height) {
  return {
    width,
    height,
    tileSize: 256,
    minLevel: 0,
    maxLevel: 6,
    getTileUrl(level, x, y) {
      return `${DEEP_ZOOM_BASE_URL}/${view}/level_${level}/tiles/${x}_${y}.jpg`;
    },
  };
}

function addMosaicOverlays(figureSize) {
  state.mosaic.clearOverlays();
  const overlayButtons = [];
  for (const [index, entry] of coordinateMap.figures.entries()) {
    const hit = element('button', 'mosaic-figure-hit');
    const figure = figures[entry.number - 1];
    hit.type = 'button';
    hit.tabIndex = index === 0 ? 0 : -1;
    hit.dataset.figureId = String(entry.number);
    hit.setAttribute('aria-label', `Open figure ${entry.number}, ${figure.name}`);
    hit.addEventListener('click', (event) => {
      event.stopPropagation();
      if (state.suppressMosaicClick === hit) {
        state.suppressMosaicClick = null;
        return;
      }
      openMosaicFigure(entry.number, hit);
    });
    hit.addEventListener('keydown', (event) => {
      if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(event.key)) return;
      const direction = ['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1;
      const nextIndex = (index + direction + coordinateMap.figures.length) % coordinateMap.figures.length;
      hit.tabIndex = -1;
      overlayButtons[nextIndex].tabIndex = 0;
      overlayButtons[nextIndex].focus();
      event.preventDefault();
    });
    const location = state.mosaic.viewport.imageToViewportRectangle(
      entry.position.x,
      entry.position.y,
      figureSize,
      figureSize,
    );
    state.mosaic.addOverlay({ element: hit, location });
    overlayButtons.push(hit);
  }
}

function handleMosaicPointerDown(event) {
  if (event.button !== 0) return;
  const hit = mosaicHitAtEvent(event);
  state.mosaicPointer = hit
    ? { hit, x: event.clientX, y: event.clientY }
    : null;
}

function handleMosaicPointerUp(event) {
  const start = state.mosaicPointer;
  state.mosaicPointer = null;
  if (!start) return;
  const hit = mosaicHitAtEvent(event);
  const distance = Math.hypot(event.clientX - start.x, event.clientY - start.y);
  if (hit !== start.hit || distance > 8) return;
  const figureId = Number(hit.dataset.figureId);
  if (!Number.isInteger(figureId)) return;
  state.suppressMosaicClick = hit;
  globalThis.setTimeout(() => {
    if (state.suppressMosaicClick === hit) state.suppressMosaicClick = null;
  }, 0);
  openMosaicFigure(figureId, hit);
}

function clearMosaicPointer() {
  state.mosaicPointer = null;
}

function mosaicHitAtEvent(event) {
  const directHit = event.target instanceof Element
    ? event.target.closest('.mosaic-figure-hit')
    : null;
  if (directHit) return directHit;
  const pointTarget = document.elementFromPoint(event.clientX, event.clientY);
  return pointTarget instanceof Element ? pointTarget.closest('.mosaic-figure-hit') : null;
}

function openMosaicFigure(figureId, opener) {
  history.pushState({ figureId }, '', `/figures/${figureId}`);
  openFigure(figureId, opener);
}

function setMosaicView(view) {
  if (!['front', 'back', 'side'].includes(view) || state.mosaicView === view) return;
  state.mosaicView = view;
  document.querySelectorAll('[data-mosaic-view]').forEach((button) => {
    button.setAttribute('aria-pressed', String(button.dataset.mosaicView === view));
  });
  if (!state.mosaic) return;
  const center = state.mosaic.viewport.getCenter();
  const zoom = state.mosaic.viewport.getZoom();
  state.mosaic.open(mosaicTileSource(view, 16 * 5533, 15 * 5533));
  state.mosaic.addOnceHandler('open', () => {
    state.mosaic.viewport.panTo(center, true);
    state.mosaic.viewport.zoomTo(zoom, null, true);
  });
}

function zoomMosaic(factor) {
  if (!state.mosaic) return;
  state.mosaic.viewport.zoomBy(factor);
  state.mosaic.viewport.applyConstraints();
}

function handleMosaicKeys(event) {
  if (!state.mosaic) return;
  const movements = {
    ArrowLeft: [-0.06, 0],
    ArrowRight: [0.06, 0],
    ArrowUp: [0, -0.06],
    ArrowDown: [0, 0.06],
  };
  if (event.key === '+' || event.key === '=') zoomMosaic(1.35);
  else if (event.key === '-') zoomMosaic(0.74);
  else if (event.key === 'Home') state.mosaic.viewport.goHome();
  else if (movements[event.key]) {
    const [x, y] = movements[event.key];
    state.mosaic.viewport.panBy(new state.openSeadragon.Point(x, y));
    state.mosaic.viewport.applyConstraints();
  } else return;
  event.preventDefault();
}

function renderTraits(traits) {
  const excluded = new Set(['NUMBER', 'NAME']);
  const nodes = [];
  for (const [label, value] of Object.entries(traits)) {
    if (excluded.has(label) || value === '') continue;
    nodes.push(element('dt', null, label), element('dd', null, String(value)));
  }
  ui.traits.replaceChildren(...nodes);
}

function updateCanonical(figureId) {
  const canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) return;
  canonical.href = figureId
    ? `${TOKEN_PAGE_BASE_URL}${figureId}`
    : `https://littlepinkmen.com${collectionPath}`;
}

function normalizedPath(pathname) {
  const normalized = pathname.replace(/\/+$/, '');
  return normalized || '/';
}

function figureIdFromPath(pathname) {
  return /^\/figures\/(\d+)\/?$/.exec(pathname)?.[1] ?? null;
}

function figureImageUrl(figureId, width) {
  const size = width <= 280 ? 280 : 500;
  return `${FIGURE_BASE_URL}/${size}/${String(figureId).padStart(3, '0')}.png`;
}

function byId(id) {
  const value = document.getElementById(id);
  if (!value) throw new Error(`Missing required element #${id}`);
  return value;
}

function element(tag, className = null, text = null) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== null) node.textContent = text;
  return node;
}

function errorText(error) {
  return error instanceof Error ? error.message : String(error);
}
